import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { LexicalCollaboration } from '@lexical/react/LexicalCollaborationContext';
import { CollaborationPlugin } from '@lexical/react/LexicalCollaborationPlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { decodeCollaborationUpdate, encodeCollaborationUpdate, hashNoteText, isCollaborationStateCurrent, UuidGenerator, } from '@standardnotes/snjs';
import { Doc, encodeStateAsUpdate } from 'yjs';
import { useApplication } from '@/Components/ApplicationProvider';
import { LocalCollaborationProvider } from './LocalCollaborationProvider';
import { RelayCollaborationProvider } from './RelayCollaborationProvider';
import { CollaborationDocumentId, getCollaborationRelayUrl, getCursorColor, getSessionAccessToken, } from './CollaborationConfig';
/**
 * Binds the Super editor to a Yjs document stored in the note, so that conflicting versions of the note
 * can be merged instead of duplicated. Used for notes in shared vaults.
 * When a collaboration relay is configured, people editing the note at the same time also see
 * each other's changes and cursors live.
 */
export function NoteCollaborationPlugin({ note, onReady }) {
    var _a, _b;
    const application = useApplication();
    const [editor] = useLexicalComposerContext();
    const [setup] = useState(() => {
        const doc = new Doc();
        const isCurrent = isCollaborationStateCurrent(note);
        const collaboration = isCurrent ? note.myRadmitClaudeCollaboration : undefined;
        const initialState = collaboration ? decodeCollaborationUpdate(collaboration.state) : undefined;
        const origin = collaboration ? collaboration.origin : UuidGenerator.GenerateUuid();
        const relayUrl = getCollaborationRelayUrl();
        const provider = relayUrl && note.shared_vault_uuid
            ? new RelayCollaborationProvider(doc, initialState, relayUrl, 
            /** the document lineage is part of the room: states of different lineages must not be mixed */
            `${note.shared_vault_uuid}/${note.uuid}/${origin}`, () => getSessionAccessToken(application))
            : new LocalCollaborationProvider(doc, initialState);
        return {
            doc,
            origin,
            provider,
            /** Text used to create the document when the note has no usable Yjs state yet */
            bootstrapText: collaboration ? undefined : note.text || undefined,
        };
    });
    const username = (_b = (_a = application.sessions.getUser()) === null || _a === void 0 ? void 0 : _a.email) !== null && _b !== void 0 ? _b : 'Anonymous';
    const cursorColor = useMemo(() => getCursorColor(username), [username]);
    /** Remote cursors are positioned inside the editor container, so they scroll with the text */
    const [cursorsContainer, setCursorsContainer] = useState(null);
    useEffect(() => {
        return editor.registerRootListener((rootElement) => {
            var _a;
            setCursorsContainer((_a = rootElement === null || rootElement === void 0 ? void 0 : rootElement.parentElement) !== null && _a !== void 0 ? _a : null);
        });
    }, [editor]);
    const cursorsContainerRef = useMemo(() => ({ current: cursorsContainer }), [cursorsContainer]);
    useEffect(() => {
        return () => setup.provider.destroy();
    }, [setup]);
    useEffect(() => {
        var _a;
        let lastKnownState = isCollaborationStateCurrent(note) ? (_a = note.myRadmitClaudeCollaboration) === null || _a === void 0 ? void 0 : _a.state : undefined;
        onReady({
            getState: (text) => {
                const state = encodeCollaborationUpdate(encodeStateAsUpdate(setup.doc));
                lastKnownState = state;
                return { state, origin: setup.origin, textHash: hashNoteText(text) };
            },
            applyNoteState: (updatedNote) => {
                const collaboration = updatedNote.myRadmitClaudeCollaboration;
                if (!collaboration || collaboration.origin !== setup.origin || !isCollaborationStateCurrent(updatedNote)) {
                    return false;
                }
                if (collaboration.state !== lastKnownState) {
                    setup.provider.applyState(decodeCollaborationUpdate(collaboration.state));
                    lastKnownState = collaboration.state;
                }
                return true;
            },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps -- registered once per document
    }, [onReady, setup]);
    const providerFactory = useCallback((id, yjsDocMap) => {
        yjsDocMap.set(id, setup.doc);
        return setup.provider;
    }, [setup]);
    if (!cursorsContainer) {
        return null;
    }
    return (_jsx(LexicalCollaboration, { children: _jsx(CollaborationPlugin, { id: CollaborationDocumentId, providerFactory: providerFactory, shouldBootstrap: true, initialEditorState: setup.bootstrapText, username: username, cursorColor: cursorColor, cursorsContainerRef: cursorsContainerRef }) }));
}
